import React, { useState, useEffect } from 'react';
import './ReferralManagement.css';
import apiClient from '../../api';
import { formatDateTime } from '../../utils/dateUtils';

const ReferralManagement = () => {
    const [userId, setUserId] = useState('');
    const [referralData, setReferralData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [allUsers, setAllUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchAllUsers();
    }, []);

    const fetchAllUsers = async () => {
        try {
            setLoadingUsers(true);
            const response = await apiClient.get('/api/user/users/admin/');
            const data = response.data;
            setAllUsers(Array.isArray(data) ? data : (data.results || []));
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchReferralData = async (id) => {
        if (!id) {
            setError('Please enter a user ID');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const response = await apiClient.get(`/api/user/referral/my-referrals/${id}/`);
            setReferralData(response.data);
        } catch (error) {
            console.error('Error fetching referral data:', error);
            setError(error.response?.data?.error || 'Failed to load referral data');
            setReferralData(null);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchReferralData(userId);
    };

    const handleUserSelect = (selectedUserId) => {
        setUserId(selectedUserId);
        fetchReferralData(selectedUserId);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        alert('Copied to clipboard!');
    };

    const filteredUsers = allUsers.filter(user =>
        (user.username && user.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.id && user.id.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="referral-management">
            <div className="referral-header">
                <h1 className="referral-title">🔗 Referral Management</h1>
                <p className="referral-subtitle">View user referral statistics and referred users</p>
            </div>

            <div className="user-selection-section">
                <h3>Select a User to View Referrals</h3>

                <div className="user-search-container">
                    <input
                        type="text"
                        placeholder="🔍 Search users by username, email, or ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="user-search-input"
                    />
                </div>

                {loadingUsers ? (
                    <div className="loading-message">Loading users...</div>
                ) : (
                    <div className="user-grid">
                        {filteredUsers.slice(0, 20).map((user) => (
                            <div
                                key={user.id}
                                className={`user-card ${userId === user.id ? 'selected' : ''}`}
                                onClick={() => handleUserSelect(user.id)}
                            >
                                <div className="user-card-avatar">
                                    {user.username ? user.username.substring(0, 2).toUpperCase() : '??'}
                                </div>
                                <div className="user-card-info">
                                    <div className="user-card-name">{user.username || 'Unknown'}</div>
                                    <div className="user-card-email">{user.email || 'No email'}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {filteredUsers.length > 20 && (
                    <p className="more-users-hint">Showing first 20 users. Use search to find specific users.</p>
                )}

                <div className="manual-id-section">
                    <p>Or enter User ID manually:</p>
                    <form onSubmit={handleSearch} className="search-form">
                        <input
                            type="text"
                            placeholder="Enter User ID (e.g., 6939d72373c3ddbc88ae73c6)"
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                            className="search-input"
                        />
                        <button type="submit" className="search-button" disabled={loading}>
                            {loading ? 'Loading...' : '🔍 Search'}
                        </button>
                    </form>
                </div>
            </div>

            {error && <div className="error-message">❌ {error}</div>}

            {loading && (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Fetching referral data...</p>
                </div>
            )}

            {referralData && !loading && (
                <div className="referral-data-section">
                    <div className="stats-grid">
                        <div className="stat-card referral-code-card">
                            <div className="stat-icon">🎫</div>
                            <div className="stat-content">
                                <h4>Referral Code</h4>
                                <div className="referral-code-display">
                                    <span className="code">{referralData.referral_code || 'Not Generated'}</span>
                                    {referralData.referral_code && (
                                        <button className="copy-button" onClick={() => copyToClipboard(referralData.referral_code)}>
                                            📋 Copy
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="stat-card referrals-card">
                            <div className="stat-icon">👥</div>
                            <div className="stat-content">
                                <h4>Total Referrals</h4>
                                <p className="stat-value">{referralData.total_referrals || 0}</p>
                                <p className="stat-hint">Users who signed up with this code</p>
                            </div>
                        </div>
                    </div>

                    <div className="referrals-table-section">
                        <h3>🧑‍🤝‍🧑 Referred Users ({referralData.referrals?.length || 0})</h3>

                        {referralData.referrals && referralData.referrals.length > 0 ? (
                            <table className="referrals-table">
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Email</th>
                                        <th>Joined Date</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {referralData.referrals.map((referral) => (
                                        <tr key={referral.id}>
                                            <td>
                                                <div className="user-cell">
                                                    <div className="referral-avatar">
                                                        {referral.username ? referral.username.substring(0, 2).toUpperCase() : '??'}
                                                    </div>
                                                    <div>
                                                        <div className="referral-username">{referral.username}</div>
                                                        <div className="referral-id">ID: {referral.id}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{referral.email || 'No email'}</td>
                                            <td>{formatDate(referral.created_at)}</td>
                                            <td>
                                                <span className={`status-badge ${referral.is_verified ? 'verified' : 'unverified'}`}>
                                                    {referral.is_verified ? '✓ Verified' : '⏳ Pending'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="empty-referrals">
                                <div className="empty-icon">📭</div>
                                <h4>No Referrals Yet</h4>
                                <p>This user hasn't referred anyone yet.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {!referralData && !loading && !error && (
                <div className="initial-state">
                    <div className="empty-icon">🔗</div>
                    <h3>Select a User</h3>
                    <p>Click on a user above or enter a user ID to view their referral statistics.</p>
                </div>
            )}
        </div>
    );
};

export default ReferralManagement;
